import { PricingPlanType } from '../types/pricingPlans';
import { PRICING_PLANS_CONFIG, PricingPlanConfig } from '../config/pricingPlans';

/**
 * Get the configuration for a specific pricing plan
 */
export const getPlanConfig = (planId: PricingPlanType): PricingPlanConfig => {
  return PRICING_PLANS_CONFIG[planId];
};

/**
 * Get CSS classes for a pricing plan
 */
export const getPlanColor = (planId: PricingPlanType): string => {
  return getPlanConfig(planId).cssClasses;
};

/**
 * Get icon name for a pricing plan
 */
export const getPlanIcon = (planId: PricingPlanType): string => {
  return getPlanConfig(planId).icon;
};

/**
 * Get theme color for a pricing plan
 */
export const getPlanThemeColor = (planId: PricingPlanType): string => {
  return getPlanConfig(planId).themeColor;
};

/**
 * Get all available pricing plan IDs
 */
export const getAllPlanIds = (): PricingPlanType[] => {
  return Object.keys(PRICING_PLANS_CONFIG) as PricingPlanType[];
};

/**
 * Get all internal plans (free, early-adopter)
 */
export const getInternalPlans = (): PricingPlanType[] => {
  return getAllPlanIds().filter(planId => getPlanConfig(planId).isInternal);
};

/**
 * Get all external plans (store, brand)
 */
export const getExternalPlans = (): PricingPlanType[] => {
  return getAllPlanIds().filter(planId => !getPlanConfig(planId).isInternal);
};

/**
 * Check if a plan is internal
 */
export const isInternalPlan = (planId: PricingPlanType): boolean => {
  return getPlanConfig(planId).isInternal;
};

/**
 * Get available upgrades for a plan
 */
export const getAvailableUpgrades = (planId: PricingPlanType): PricingPlanType[] => {
  return getPlanConfig(planId).upgradeTo;
};

/**
 * Check if plan A can upgrade to plan B
 */
export const canUpgradeTo = (fromPlan: PricingPlanType, toPlan: PricingPlanType): boolean => {
  return getAvailableUpgrades(fromPlan).includes(toPlan);
};

/**
 * Check if plan A is a downgrade from plan B
 */
export const isDowngrade = (fromPlan: PricingPlanType, toPlan: PricingPlanType): boolean => {
  const planHierarchy = ['free', 'store', 'brand', 'advanced-store'];
  const fromIndex = planHierarchy.indexOf(fromPlan);
  const toIndex = planHierarchy.indexOf(toPlan);
  return toIndex < fromIndex;
};

const LIMITED_FEATURE_KEYS = new Set([
  'pricing.free.features.bookings',
  'pricing.free.features.b2bLimit',
  'pricing.free.features.sms_reminders',
  'pricing.free.features.customer_files',
  'pricing.free.features.sales_tracking',
  'pricing.features.limited_billing',
]);

export const isLimitedFeature = (featureKey: string): boolean =>
  LIMITED_FEATURE_KEYS.has(featureKey) || featureKey.includes('.limited_');

/** Full-access features first, then limited. Preserves relative order inside each group. */
export const sortIncludedFeaturesByAccess = <T extends { key: string }>(features: T[]): T[] =>
  [...features].sort((a, b) => Number(isLimitedFeature(a.key)) - Number(isLimitedFeature(b.key)));

/**
 * Get theme color classes for UI components
 */
export const getThemeColorClasses = (themeColor: string) => {
  switch (themeColor) {
    case 'emerald':
      return {
        text: 'text-emerald-600',
        bg: 'bg-emerald-600',
        lightBg: 'bg-emerald-50',
        border: 'border-emerald-200'
      };
    case 'blue':
      return {
        text: 'text-blue-600',
        bg: 'bg-blue-600',
        lightBg: 'bg-blue-50',
        border: 'border-blue-200'
      };
    case 'purple':
      return {
        text: 'text-purple-600',
        bg: 'bg-purple-600',
        lightBg: 'bg-purple-50',
        border: 'border-purple-200'
      };
    case 'orange':
      return {
        text: 'text-orange-600',
        bg: 'bg-orange-600',
        lightBg: 'bg-orange-50',
        border: 'border-orange-200'
      };
    case 'amber':
      return {
        text: 'text-amber-600',
        bg: 'bg-amber-600',
        lightBg: 'bg-amber-50',
        border: 'border-amber-200'
      };
    default:
      return {
        text: 'text-gray-600',
        bg: 'bg-gray-600',
        lightBg: 'bg-gray-50',
        border: 'border-gray-200'
      };
  }
};

export const generatePricingPlan = (
  planId: PricingPlanType, 
  isCurrentPlan: boolean = false
) => {
  const config = getPlanConfig(planId);
  
  return {
    id: planId,
    name: config.name,
    description: config.description,
    features: config.features,
    featuresNotIncluded: config.featuresNotIncluded,
    note: config.note,
    price: config.price,
    isCurrentPlan,
    isUpgradeable: config.upgradeTo.length > 0,
    upgradeTo: config.upgradeTo,
    themeColor: config.themeColor,
    cssClasses: config.cssClasses,
    icon: config.icon,
    isInternal: config.isInternal,
    maxBookingsPerWeek: config.maxBookingsPerWeek,
    maxB2BOrdersPerMonth: config.maxB2BOrdersPerMonth
  };
};
